import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

function Status({ ok, children }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${ok ? "text-emerald-700" : "text-amber-700"}`}>
      {ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} {children}
    </div>
  );
}

export default function AdminConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ notification_email: "", razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "" });
  const load = () => api.get("/admin/config").then((r) => { setCfg(r.data); setForm((f) => ({ ...f, notification_email: r.data.notification_email || "" })); });
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (fields) => {
    const payload = Object.fromEntries(fields.map((k) => [k, form[k]]).filter(([, v]) => v !== ""));
    if (fields.includes("notification_email")) payload.notification_email = form.notification_email;
    try {
      await api.put("/admin/config", payload);
      toast.success("Saved");
      setForm((f) => ({ ...f, razorpay_key_secret: "", razorpay_webhook_secret: "" }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cfg) return <Skeleton className="h-64" />;
  const rz = cfg.razorpay;
  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="admin-config-tab">
      <Card className="border-border/70">
        <CardContent className="space-y-4 p-6">
          <h3 className="font-display text-lg font-bold">Enquiry notification email</h3>
          <Status ok={cfg.email_configured && !!cfg.notification_email}>
            {cfg.email_configured ? `New enquiries are emailed to ${cfg.notification_email || "— (not set)"}` : "Email service is not configured"}
          </Status>
          <div>
            <Label>Admin notification email address</Label>
            <Input type="email" value={form.notification_email} onChange={set("notification_email")} placeholder="admissions@yourdomain.com" className="mt-1.5" data-testid="config-notification-email" />
            <p className="mt-1 text-xs text-muted-foreground">Enquiries are always saved in the dashboard even if email delivery fails.</p>
          </div>
          <Button onClick={() => save(["notification_email"])} className="bg-[hsl(var(--brand-orange))]" data-testid="config-save-email">Save email</Button>
        </CardContent>
      </Card>

      <Card className="border-border/70">
        <CardContent className="space-y-4 p-6">
          <h3 className="font-display text-lg font-bold">Razorpay credentials</h3>
          <Status ok={rz.configured}>{rz.configured ? `Connected in ${rz.mode.toUpperCase()} mode (key ${rz.key_id})` : "Not configured — checkout runs in MOCK mode"}</Status>
          {rz.configured && <div className="text-xs text-muted-foreground">Secret: <span className="font-mono">{rz.key_secret_masked}</span> · source: {rz.source}</div>}
          <div className="grid gap-3">
            <div><Label>Key ID</Label><Input value={form.razorpay_key_id} onChange={set("razorpay_key_id")} placeholder="rzp_live_… or rzp_test_…" className="mt-1.5" data-testid="config-rzp-key-id" /></div>
            <div><Label>Key Secret</Label><Input type="password" value={form.razorpay_key_secret} onChange={set("razorpay_key_secret")} placeholder="Leave blank to keep current" className="mt-1.5" data-testid="config-rzp-key-secret" /></div>
          </div>
          <Button onClick={() => save(["razorpay_key_id", "razorpay_key_secret"])} disabled={!form.razorpay_key_id && !form.razorpay_key_secret} className="bg-[hsl(var(--brand-navy))]" data-testid="config-save-rzp">Update keys</Button>
          <p className="text-xs text-muted-foreground">Secrets are stored on the server only and never sent to the browser. Get keys from Razorpay Dashboard → Account & Settings → API Keys.</p>
        </CardContent>
      </Card>

      <Card className="border-border/70 lg:col-span-2">
        <CardContent className="space-y-4 p-6">
          <h3 className="font-display text-lg font-bold">Razorpay webhook (recommended)</h3>
          <Status ok={rz.webhook_configured}>{rz.webhook_configured ? "Webhook secret configured — payments are confirmed even if a student closes the browser" : "Webhook not configured yet"}</Status>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Razorpay Dashboard → Account & Settings → Webhooks → <b>Add New Webhook</b></li>
            <li>Webhook URL: <span className="font-mono text-foreground" data-testid="config-webhook-url">{rz.webhook_url}</span> <button type="button" onClick={() => { navigator.clipboard.writeText(rz.webhook_url); toast.success("Copied"); }}><Copy className="inline h-3.5 w-3.5" /></button></li>
            <li>Create a secret, select events <b>payment.captured</b>, <b>payment.failed</b>, <b>order.paid</b></li>
            <li>Paste the same secret below and save.</li>
          </ol>
          <div className="flex flex-wrap gap-2">
            <Input type="password" value={form.razorpay_webhook_secret} onChange={set("razorpay_webhook_secret")} placeholder="Webhook secret" className="max-w-sm" data-testid="config-webhook-secret" />
            <Button onClick={() => save(["razorpay_webhook_secret"])} disabled={!form.razorpay_webhook_secret} data-testid="config-save-webhook">Save secret</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
